import { useEffect, useRef, useState } from "react";
import LogUtil from "@/betby/utils/LogUtil";
import { setupUnityMessageListener } from "@/betby/models/host/unityMessageService";
import timeManager from "@/betby/utils/TimeManager";
import { TRACK_EVENT_SDK_START_PAGE_SPM } from "@/betby/utils/TrackEvents";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";
import { appConfig } from "@/betby/infrastructure/config/appConfig";
import { getHttpRuntime } from "@/betby/infrastructure/http/httpRuntime";
import { startTokenRefreshTimer } from "@/betby/api/http";
import { ensureAuthHttpRuntimeRegistered } from "@/betby/models/auth/authInitializationService";
import { syncAdminToken } from "@/betby/infrastructure/http/authTokenStorage";
import {
  createDefaultUnityMessageRuntime,
  registerUnityMessageRuntime,
} from "@/betby/infrastructure/host/unityMessageRuntime";
import {
  createDefaultGlobalUiRuntime,
  registerGlobalUiRuntime,
} from "@/betby/infrastructure/ui/globalUiRuntime";
import {
  createDefaultAuthRuntime,
  registerAuthRuntime,
} from "@/betby/infrastructure/auth/authRuntime";

export function useAppBootstrapViewModel() {
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    registerAuthRuntime(createDefaultAuthRuntime());
    ensureAuthHttpRuntimeRegistered();
    registerUnityMessageRuntime(createDefaultUnityMessageRuntime());
    registerGlobalUiRuntime(createDefaultGlobalUiRuntime());
    const cleanupListener = setupUnityMessageListener();
    let active = true;

    if (!initPromiseRef.current) {
      initPromiseRef.current = (async () => {
        try {
          analyticsBridge.track(TRACK_EVENT_SDK_START_PAGE_SPM, { src: 516 });
        } catch (e) {
          LogUtil.error("[SDKInit] Failed to track start page exposure", e);
        }

        await Promise.allSettled([
          (async () => {
            try {
              await analyticsBridge.init({
                debug: false,
                productId: appConfig.analytics.productId,
                endpoint: appConfig.analytics.endpoint,
                pureTracking: true,
                hasAppId: false,
                aesKey: appConfig.analytics.aesKey,
                aesIv: appConfig.analytics.aesIv,
                gtmEnabled: false,
                deferInitEventsUntilFirstUpdate: true,
                autoTrackCoreEvents: false,
              });
              LogUtil.info("Spark Analytics SDK initialized successfully");
            } catch (error) {
              LogUtil.error("Failed to initialize Spark Analytics SDK", error);
            }
          })(),
          timeManager.initSystemTime(),
        ]);

        const authSnapshot = getHttpRuntime().getAuthSnapshot?.();
        syncAdminToken(authSnapshot?.accessToken ?? null);
        if (authSnapshot?.isAuthenticated && authSnapshot.expiresTime) {
          const currentServerTime = timeManager.getCurrentTimestamp();
          LogUtil.info(
            `[SDKInit] App initialized, starting token refresh timer. Expires: ${authSnapshot.expiresTime}, ServerTime: ${currentServerTime}`,
          );
          startTokenRefreshTimer(authSnapshot.expiresTime, currentServerTime);
        }
      })();
    }

    void initPromiseRef.current.finally(() => {
      if (active) {
        setIsReady(true);
      }
    });

    return () => {
      active = false;
      registerAuthRuntime(null);
      registerUnityMessageRuntime(null);
      registerGlobalUiRuntime(null);
      cleanupListener?.();
    };
  }, []);

  return { isReady };
}
